/**
 * The user's own live sessions — one per platform — with remote sign-out.
 *
 * Not gated by any Access panel: everyone may see and end their own sessions.
 * The admin view over OTHER people's history is LoginSessions.tsx, which is
 * gated behind the `login_sessions` panel.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../api/api";

interface SessionRow {
  platform: string;
  device_id: string;
  device_name: string | null;
  os: string | null;
  client_version: string | null;
  ip_address: string | null;
  claimed_at: string | null;
  last_seen_at: string | null;
  is_current: boolean;
}

const PLATFORM_LABELS: Record<string, string> = {
  teamlio_web: "Teamlio Web",
  messenger_web: "Messenger (Browser)",
  messenger_desktop: "Messenger Desktop",
  workmonitor: "Work Monitor",
};

function when(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export default function ActiveDevices() {
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/sessions");
      setRows(data?.sessions ?? []);
      setError(null);
    } catch {
      setError("Could not load your sessions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (platform: string) => {
    setBusy(platform);
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.delete(`/api/auth/sessions/${platform}`);
      await load();
      if (!data?.revoked) {
        // A normal outcome, not a failure: either nothing was there to
        // revoke, or another sign-in took the slot mid-request. The list
        // is stale either way, so we still reload above.
        setNotice(
          "That device wasn't signed out — its session had already ended, or another sign-in took it over first.",
        );
      }
    } catch {
      setError("Could not sign that device out.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="p-6">Loading your sessions…</div>;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Active devices</h1>
        <p className="text-sm text-muted-foreground">
          You can be signed in to each app on one device at a time. Signing in
          somewhere new signs the previous device out.
        </p>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {notice && <div className="text-sm text-muted-foreground">{notice}</div>}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.platform}
            className="flex items-center justify-between rounded-lg border p-4"
          >
            <div>
              <div className="font-medium">
                {PLATFORM_LABELS[r.platform] ?? r.platform}
                {r.is_current && (
                  <span className="ml-2 text-xs text-green-600">This device</span>
                )}
              </div>
              <div className="text-sm">{r.device_name || "Unknown device"}</div>
              <div className="text-xs text-muted-foreground">
                {[r.os, r.client_version].filter(Boolean).join(" · ")}
                {/* IP rendered in its own node (not joined into the string
                    above) so it remains an exact text-content match on its
                    own — useful for tests and for copy/selection. */}
                {(r.os || r.client_version) && r.ip_address ? " · " : ""}
                {r.ip_address && <span>{r.ip_address}</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                Signed in {when(r.claimed_at)} · Last seen {when(r.last_seen_at)}
              </div>
            </div>
            {!r.is_current && (
              <button
                type="button"
                className="rounded-md border px-3 py-1.5 text-sm"
                disabled={busy === r.platform}
                onClick={() => void revoke(r.platform)}
              >
                {busy === r.platform ? "Signing out…" : "Sign out"}
              </button>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="text-sm text-muted-foreground">
            No active sessions recorded yet.
          </div>
        )}
      </div>
    </div>
  );
}
